import React, { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, CheckCircle2, Info, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { fmtEUR, fmtDateTime } from "@/lib/constants";

export const PriceSuggestionCard = ({ vehicle, reload }) => {
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const sug = vehicle.price_suggestion;

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/price-suggestion`, {});
      await reload();
      toast.success("KI-Preisvorschlag aktualisiert");
    } catch (e) {
      toast.error(apiError(e, "Der KI-Preisvorschlag ist momentan nicht verfügbar"));
    } finally {
      setBusy(false);
    }
  };

  const applyPrice = async () => {
    setApplying(true);
    try {
      await api.patch(`/vehicles/${vehicle.id}`, { price: sug.empfohlener_preis, source: "ki_preisvorschlag" });
      await reload();
      toast.success(`Preis übernommen: ${fmtEUR(sug.empfohlener_preis)}`);
    } catch (e) {
      toast.error(apiError(e, "Preis konnte nicht übernommen werden"));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card className="border-primary/25" data-testid="overview-price-suggestion-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" /> KI-Preisvorschlag
          <span className="rounded-full border border-primary/30 bg-primary/10 text-primary text-[10px] px-2 py-0.5 font-normal">KI-Schätzung</span>
        </CardTitle>
        <Button size="sm" variant="outline" className="h-8 gap-1.5 border-primary/40 text-primary hover:bg-primary/10"
          disabled={busy} onClick={generate} data-testid="overview-price-suggest-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : sug ? <RefreshCw className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
          {sug ? "Neu berechnen" : "Erstellen"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-2.5">
        {busy && (
          <div className="space-y-2">
            <div className="h-6 w-44 rounded bg-secondary/60 animate-pulse" />
            <div className="h-3 w-full rounded bg-secondary/60 animate-pulse" />
            <div className="h-3 w-2/3 rounded bg-secondary/60 animate-pulse" />
          </div>
        )}
        {!sug && !busy && (
          <p className="text-sm text-muted-foreground" data-testid="overview-price-suggestion-empty">
            Noch keine Schätzung vorhanden. Die KI gibt eine Preisorientierung auf Basis von Marke, Modell, Baujahr, Kilometerstand, Ausstattung und Zustand – ohne Live-Marktdaten.
          </p>
        )}
        {sug && !busy && (
          <div className="space-y-2.5" data-testid="overview-price-suggestion-result">
            <div className="flex items-end gap-5 flex-wrap">
              <div>
                <div className="text-[11px] text-muted-foreground">Preisspanne</div>
                <div className="font-display text-lg font-bold tabular-nums" data-testid="overview-price-suggestion-range">
                  {fmtEUR(sug.preis_von)} – {fmtEUR(sug.preis_bis)}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Empfehlung</div>
                <div className="font-display text-lg font-bold text-primary tabular-nums" data-testid="overview-price-suggestion-recommended">
                  {fmtEUR(sug.empfohlener_preis)}
                </div>
              </div>
              {vehicle.price !== sug.empfohlener_preis && (
                <Button size="sm" className="gap-1.5" disabled={applying} onClick={applyPrice} data-testid="overview-price-suggestion-apply">
                  {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Als Preis übernehmen
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground" data-testid="overview-price-suggestion-reason">{sug.begruendung}</p>
            {(sug.einflussfaktoren || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {sug.einflussfaktoren.slice(0, 6).map((f, i) => (
                  <span key={i} className={`rounded-full border px-2 py-0.5 text-[10px] ${
                    f.wirkung === "positiv" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                    : f.wirkung === "negativ" ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                    : "border-border bg-secondary/40 text-muted-foreground"}`}>
                    {f.faktor}
                  </span>
                ))}
              </div>
            )}
            <p className="text-[10px] text-muted-foreground flex items-start gap-1" data-testid="overview-price-suggestion-disclaimer">
              <Info className="h-3 w-3 mt-0.5 shrink-0" /> {sug.disclaimer} {sug.generated_at ? `· Stand: ${fmtDateTime(sug.generated_at)}` : ""}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
